import React, { useRef, useState, useCallback } from 'react';
import { type DocumentElement as DocumentElementType, isElementHidden } from '@eaco/forms-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-common-types';
import DocumentElement from '../DocumentView/DocumentElement';
import { useIsEditor, useIsReadOnly, useResolvedStateMap } from '../DocumentView/DocumentContext';
import { cn } from '../lib/utils';

interface Props {
  element: DocumentElementType;
}

/**
 * Tabs container — an underline tab bar (Elements Library): the active tab is
 * accent-coloured with a thicker accent underline overlapping the bar's own
 * hairline border; inactive tabs are muted. Only the active tab's children
 * render below.
 */
const Tabs: React.FC<Props> = ({ element }) => {
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const resolvedState = useResolvedStateMap();
  // Archived and hidden tabs render only on the editor canvas; Tabs renders
  // its own children, so the dispatcher's guards never see a tab. Hidden
  // follows the fill precedence (a fired rule beats the static flag).
  const tabs = (element.elements || []).filter(
    (t) => isEditor || (!t.archived && !isElementHidden(t, resolvedState as Map<string, never>)),
  );
  const defaultTab = tabs[0]?.id || '';
  const [active, setActive] = useState(defaultTab);
  const activeId = tabs.some((t) => t.id === active) ? active : defaultTab;
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleTabChange = useCallback(
    (value: string) => {
      setActive(value);
      const tabElement = tabRefs.current.get(value);
      const container = scrollContainerRef.current;
      if (!tabElement || !container) return;
      const tabIndex = tabs.findIndex((t) => t.id === value);
      if (tabIndex === 0) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (tabIndex === tabs.length - 1) {
        container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
      } else {
        tabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    },
    [tabs],
  );

  if (tabs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-muted-foreground/25 p-6 text-center">
        <p className="text-sm text-muted-foreground italic">
          No tabs added yet
        </p>
      </div>
    );
  }

  const activeChildren = tabs.find((t) => t.id === activeId)?.elements || [];

  // Viewing a result (response page, sign-off, print): every tab stacks
  // under its own heading, like Pages does, so nothing hides behind a tab
  // the reviewer has not clicked.
  if (isReadOnly) {
    return (
      <div className="w-full space-y-6">
        {tabs.map((tab) => {
          const tabData = tab.data as { name?: string } | undefined;
          const tabName = tabData?.name || tab.name;
          const children = tab.elements || [];
          return (
            <section key={tab.id} className="space-y-4">
              <h4 className="border-b-[1.5px] border-[var(--forms-page)] pb-[6px] text-[length:var(--forms-tab-font)] font-bold text-[var(--forms-ink)]">
                {tabName}
              </h4>
              {children.length === 0 ? (
                <p className="py-2 text-sm italic text-muted-foreground">
                  No elements in this tab
                </p>
              ) : (
                children.map((child) => (
                  <DocumentElement key={child.id} element={child} />
                ))
              )}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-max min-w-full border-b-[1.5px] border-[var(--forms-page)]">
          {tabs.map((tab) => {
            const tabData = tab.data as
              | { name?: string; icon?: string }
              | undefined;
            const tabName = tabData?.name || tab.name;
            const tabIcon = tabData?.icon as IconName | undefined;
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                ref={(el) => {
                  if (el) tabRefs.current.set(tab.id, el);
                  else tabRefs.current.delete(tab.id);
                }}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap px-[var(--forms-input-px)] py-[9px] text-[length:var(--forms-tab-font)] font-bold transition-colors',
                  isActive
                    ? '-mb-[1.5px] border-b-[2.5px] border-[var(--forms-accent,var(--forms-accent-default))] text-[var(--forms-accent,var(--forms-accent-default))]'
                    : 'text-[var(--forms-muted-soft)] hover:text-[var(--forms-ink)]',
                )}
              >
                {tabIcon && (
                  <FontAwesomeIcon
                    icon={['far', tabIcon] as any}
                    className="h-3.5 w-3.5"
                  />
                )}
                {tabName}
              </button>
            );
          })}
        </div>
      </div>
      <div className="w-full space-y-4 pt-4">
        {activeChildren.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">
            No elements in this tab
          </p>
        ) : (
          activeChildren.map((child) => (
            <DocumentElement key={child.id} element={child} />
          ))
        )}
      </div>
    </div>
  );
};

export default Tabs;
